/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { Formik, Field } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { FormGroup, TextboxField, Modal, Tooltip, } from '@tradingblock/components';
import { DashboardSettingsPath, DefaultGridLayout, StorageBlocksPath, StorageGridPath, UiSettings } from '@tradingblock/types';
import { useDispatcher } from './../../data/global/hooks';
import { useStateSelector, account, dashboardsSelectors } from '../../data/global/dataSelectors';
import { useStore } from './../../context/Storage';
import { useDashboardSettings, useDashboardSettingsUpdater } from './../../hooks/useDashboardSettings';
import { GenericField } from '../form/GenericField';
import { Config } from '../../config';
import { useVirtualTrading } from '../../hooks/useVirtualTrading';
import { useSelector } from 'react-redux';
import { InitialState } from '../../data/global/initialState';

export const DashboardSettings: React.FC<{ show?: boolean; toggleModal: () => void }> = ({ show, toggleModal }) => {
  const { columns, ...initialSettings } = useDashboardSettings();
  const { updateSettings } = useDashboardSettingsUpdater();
  const { isVirtualEnv } = useVirtualTrading();
  const { dispatcher } = useDispatcher();
  const [shouldResetSettings, setShouldResetSettings] = useState(false);
  const [shouldDisableSubmit, setShouldDisableSubmit] = useState(false);
  const dashboardId = useSelector(dashboardsSelectors.dashboardId);
  const accountId = useStateSelector(account.accountId);
  const store = useStore();

  const initialUiState = InitialState.settings.ui;
  const defaultDashboardSettings = {
    ...DefaultGridLayout,
    muteAllSounds: initialUiState.muteAllSounds,
    showBannerSymbols: initialUiState.showBannerSymbols,
    bannerSymbols: initialUiState.bannerSymbols,
    afterHoursTrading: initialUiState.afterHoursTrading,
    balancePollingThrottleMs: initialUiState.balancePollingThrottleMs,
    feedSyncIntervalMs: initialUiState.feedSyncIntervalMs,
    cashieringPollingInterval: initialUiState.cashieringPollingInterval,
    cashieringPendingReminderInterval: initialUiState.cashieringPendingReminderInterval,
    cashieringPendingReminderMaxAttempts: initialUiState.cashieringPendingReminderMaxAttempts,
    hotkeys: initialUiState.hotkeys,
  };

  useEffect(() => {
    const tryReset = async () => {
      const approvedSettingsReset = shouldResetSettings && window.confirm(
        'Are you sure you want to reset dashboard settings?\nAll settings will be return to default values.\nContinue?'
      );
      if (dashboardId && approvedSettingsReset) {
        if (accountId) {
          dispatcher.settings.setSettings(accountId, defaultDashboardSettings);
        }
      }
      if (approvedSettingsReset) toggleModal();
    };
    if (store && (shouldResetSettings)) tryReset();
    return () => {
      setShouldDisableSubmit(false);
      setShouldResetSettings(false);
    }
  }, [dashboardId, shouldResetSettings, shouldDisableSubmit, store, toggleModal]);

  const onSubmit = useCallback(
    (values: UiSettings, { setSubmitting }) => {
      const settings = {
        ...values,
        gridColumnsLarge: _.toNumber(values.gridColumnsLarge),
        gridColumnsMedium: _.toNumber(values.gridColumnsMedium),
        gridColumnsSmall: _.toNumber(values.gridColumnsSmall),
        width: _.toNumber(values.width),
        feedSyncIntervalMs: _.toNumber(values.feedSyncIntervalMs),
        cashieringPollingInterval: _.toNumber(values.cashieringPollingInterval),
        cashieringPendingReminderInterval: _.toNumber(values.cashieringPendingReminderInterval),
        cashieringPendingReminderMaxAttempts: _.toNumber(values.cashieringPendingReminderMaxAttempts),
      };
      !shouldDisableSubmit && updateSettings(settings);
      !shouldDisableSubmit && toggleModal();
    },
    [updateSettings, toggleModal]
  );
  const onCancel = useCallback(() => {
    toggleModal();
  }, [toggleModal]);
  const canViewCashieringAdmin = useStateSelector(account.hasCashieringAdminPermission);
  return (
    <Formik
      initialValues={initialSettings}
      enableReinitialize={true}
      validate={values => {
        const numberFields: any[] = ['gridColumnsLarge', 'gridColumnsMedium', 'gridColumnsSmall', 'width', 'feedSyncIntervalMs'];
        return _.reduce(
          numberFields,
          (errors: any, field: keyof UiSettings) => {
            const val = _.toNumber(values[field]);
            if (_.isNaN(val)) {
              setShouldDisableSubmit(true)
              return { ...errors, [field]: `${field} must equal a number` };
            } else if (field === 'feedSyncIntervalMs' && (val < 500 || val > 1000000000)) {
              setShouldDisableSubmit(true)
              return { ...errors, [field]: `${field} must be between 500 and 1000000000` };
            }
            else {
              setShouldDisableSubmit(false)
            }
            return errors;
          },
          {}
        );
      }}
      onSubmit={onSubmit}
    >
      {({ values, errors, handleSubmit }) => (
        <Modal setShow={() => toggleModal()} show={show}>
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>Dashboard settings</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <form id="settings" onSubmit={(e) => {
              e.preventDefault()
              handleSubmit()
            }}>
              <h5>Grid</h5>
              <FormGroup>
                <Field
                  component={TextboxField}
                  name={'gridColumnsLarge'}
                  id={'gridColumnsLarge'}
                  label="Grid columns for large display"
                />
                {errors && errors.gridColumnsLarge && <p className="error">{errors.gridColumnsLarge}</p>}

                <Field
                  component={TextboxField}
                  name={'gridColumnsMedium'}
                  id={'gridColumnsMedium'}
                  label="Grid columns for medium display"
                />
                {errors && errors.gridColumnsMedium && <p className="error">{errors.gridColumnsLarge}</p>}

                <Field
                  component={TextboxField}
                  name={'gridColumnsSmall'}
                  id={'gridColumnsSmall'}
                  label="Grid columns for small display"
                />
                {errors && errors.gridColumnsSmall && <p className="error">{errors.gridColumnsLarge}</p>}

                <Field component={TextboxField} name={'width'} id={'width'} label="Grid width" />
                {errors && errors.width && <p className="error">{errors.width}</p>}
              </FormGroup>
              <h5>Trading</h5>
              <FormGroup>
                {!Config.isProduction && (
                  <GenericField
                    name="afterHoursTrading"
                    value={values.afterHoursTrading}
                    settings={{ type: 'boolean', label: 'Enable trading after market close' }}
                  />
                )}
              </FormGroup>
              <h5>Data</h5>
              <FormGroup>
                <GenericField
                  name="feedSyncIntervalMs"
                  value={values.feedSyncIntervalMs}
                  settings={{ type: 'string', label: 'Quote update interval (milliseconds)' }}
                />
                {(values.feedSyncIntervalMs < 500 || values.feedSyncIntervalMs > 1000000000) && <p className="error">{'Quote update interval must be between 500 and 1000000000.'}</p>}
                {errors && errors.feedSyncIntervalMs && isNaN(values.feedSyncIntervalMs) && <p className="error">{'Quote update interval must be a number.'}</p>}
              </FormGroup>
              <FormGroup>
                {canViewCashieringAdmin && (
                  <>
                    <GenericField
                      name="cashieringPollingInterval"
                      value={values.cashieringPollingInterval}
                      settings={{ type: 'string', label: 'Cashiering update poll interval (seconds)' }}
                    />
                    <GenericField
                      name="cashieringPendingReminderInterval"
                      value={values.cashieringPendingReminderInterval}
                      settings={{ type: 'number', label: 'Cashiering pending event reminder interval (seconds)' }}
                    />
                    <GenericField
                      name="cashieringPendingReminderMaxAttempts"
                      value={values.cashieringPendingReminderMaxAttempts}
                      settings={{ type: 'number', label: 'Cashiering pending event reminder max attempts' }}
                    />
                  </>
                )}
              </FormGroup>
              <h5>Search Banner Symbols</h5>
              <FormGroup>
                <GenericField
                  name="pickBannerSymbols"
                  value={values.bannerSymbols}
                  settings={{ type: 'symbolPicker' }}
                />
              </FormGroup>
              <h5>Miscellaneous</h5>
              <FormGroup>
                <GenericField
                  name="hotkeys"
                  value={values.hotkeys}
                  settings={{ type: 'boolean', label: 'Enable Hotkeys' }}
                />
                <Tooltip
                  effect="solid"
                  id="hotkeysMessage"
                  content={
                    <ul>
                      <li>ALT+E - Toggle Dashboard lock</li>
                      <li>ALT+S - Toggle Dashboard settings</li>
                      <li>ALT+O - Create Order block</li>
                    </ul>
                  }
                  type="light"
                >
                  <button className="btn" type="button" style={{ paddingLeft: 0 }}>
                    (see hotkey shortcuts)
                  </button>
                </Tooltip>
                <br />
                <GenericField
                  name="muteAllSounds"
                  value={values.muteAllSounds}
                  settings={{ type: 'boolean', label: 'Mute All Sounds' }}
                />
                <br />
                <GenericField
                  name="showBannerSymbols"
                  value={values.showBannerSymbols}
                  settings={{ type: 'boolean', label: 'Enable Banner Symbols' }}
                />
                <br />
              </FormGroup>
            </form>
            <span style={{ display: 'flex', justifyContent: 'left' }}>
              {!Config.isProduction && !isVirtualEnv && (
                <button className="nav-link" type="button" onClick={() => setShouldResetSettings(true)}>
                  <i className="fal fa-arrow-left" /> Reset Settings
                </button>
              )}
            </span>
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="modal-footer-justified">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" disabled={shouldDisableSubmit} onClick={() => handleSubmit()}>
              Save
            </Button>
          </BootstrapModal.Footer>
        </Modal>
      )}
    </Formik>
  );
};
